import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiTruck, FiPhone, FiMapPin,
  FiDollarSign, FiClock, FiBarChart2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { busAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const EXPENSE_TYPES = ['Fuel', 'Repair & Maintenance', 'Service', 'Other'];

const emptyBusForm = {
  busNumber: '', driverName: '', driverContact: '', route: '', vehicleDetails: '', remarks: '',
};
const emptyExpenseForm = {
  type: 'Fuel', description: '', amount: '', date: '', remarks: '',
};

const inputClass =
  'w-full px-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700';
const labelClass =
  'block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5';

const AdminBuses = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'store_management';

  const [activeTab, setActiveTab] = useState('buses');

  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Reports tab
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportBusId, setReportBusId] = useState(''); // '' = all buses (fleet)
  const [fleetReport, setFleetReport] = useState(null);
  const [busReport, setBusReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Bus add/edit modal
  const [busModalOpen, setBusModalOpen] = useState(false);
  const [busForm, setBusForm] = useState(emptyBusForm);
  const [editingBus, setEditingBus] = useState(null);
  const [saving, setSaving] = useState(false);

  // Detail / expense history modal
  const [detailBus, setDetailBus] = useState(null);
  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm);
  const [addingExpense, setAddingExpense] = useState(false);

  const fetchBuses = useCallback(async () => {
    try {
      const res = await busAPI.getAll(search ? { search } : undefined);
      setBuses(res.data?.data?.buses || []);
    } catch {
      toast.error('Failed to load buses.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchBuses, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchBuses, search]);

  const openCreate = () => {
    setEditingBus(null);
    setBusForm(emptyBusForm);
    setBusModalOpen(true);
  };

  const openEdit = (bus) => {
    setEditingBus(bus);
    setBusForm({
      busNumber: bus.busNumber || '',
      driverName: bus.driverName || '',
      driverContact: bus.driverContact || '',
      route: bus.route || '',
      vehicleDetails: bus.vehicleDetails || '',
      remarks: bus.remarks || '',
    });
    setBusModalOpen(true);
  };

  const handleBusSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingBus) {
        await busAPI.update(editingBus._id, busForm);
        toast.success('Bus updated.');
      } else {
        await busAPI.create(busForm);
        toast.success('Bus added.');
      }
      setBusModalOpen(false);
      fetchBuses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save bus.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBus = async (id) => {
    if (!window.confirm('Delete this bus and its complete history?')) return;
    try {
      await busAPI.delete(id);
      setBuses((prev) => prev.filter((b) => b._id !== id));
      toast.success('Bus deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete bus.');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount) {
      toast.error('Please enter an amount.');
      return;
    }
    setAddingExpense(true);
    try {
      const res = await busAPI.addExpense(detailBus._id, {
        ...expenseForm,
        amount: Number(expenseForm.amount),
        date: expenseForm.date || undefined,
      });
      const updated = res.data?.data?.bus;
      setDetailBus(updated);
      setBuses((prev) => prev.map((b) => (b._id === updated._id ? updated : b)));
      setExpenseForm(emptyExpenseForm);
      toast.success('Expense record added.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add expense.');
    } finally {
      setAddingExpense(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense record?')) return;
    try {
      const res = await busAPI.deleteExpense(detailBus._id, expenseId);
      const updated = res.data?.data?.bus;
      setDetailBus(updated);
      setBuses((prev) => prev.map((b) => (b._id === updated._id ? updated : b)));
      toast.success('Expense record deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete expense.');
    }
  };

  const totalExpenses = (bus) =>
    (bus.expenses || []).reduce((sum, exp) => sum + (exp.amount || 0), 0);

  const fetchReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const params = {};
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportBusId) {
        const res = await busAPI.getReport(reportBusId, params);
        setBusReport(res.data?.data);
        setFleetReport(null);
      } else {
        const res = await busAPI.getFleetReport(params);
        setFleetReport(res.data?.data);
        setBusReport(null);
      }
    } catch {
      toast.error('Failed to load report.');
    } finally {
      setReportLoading(false);
    }
  }, [reportFrom, reportTo, reportBusId]);

  useEffect(() => {
    if (activeTab === 'reports') fetchReport();
  }, [activeTab, fetchReport]);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Bus Management</h2>
          <p className="text-slate-500 text-sm mt-1">
            Buses, drivers, routes and complete cost & service history
          </p>
        </div>
        {canEdit && activeTab === 'buses' && (
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
            <FiPlus className="w-4 h-4" /> Add Bus
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white border border-blue-100 rounded-2xl p-1.5 shadow-sm w-fit">
        <button onClick={() => setActiveTab('buses')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            activeTab === 'buses' ? 'bg-[#1D4ED8] text-white' : 'text-slate-500 hover:bg-blue-50'
          }`}>
          <FiTruck className="w-4 h-4" /> Buses
        </button>
        <button onClick={() => setActiveTab('reports')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            activeTab === 'reports' ? 'bg-[#1D4ED8] text-white' : 'text-slate-500 hover:bg-blue-50'
          }`}>
          <FiBarChart2 className="w-4 h-4" /> Reports
        </button>
      </div>

      {activeTab === 'buses' && (
      <>
      {/* Search */}
      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-4 items-center shadow-sm">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search by bus number, driver or route..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
        <div className="text-sm text-slate-400 flex-shrink-0">
          {buses.length} bus{buses.length !== 1 ? 'es' : ''}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Bus</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Driver</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Route</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Total Expenses</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {buses.map((bus, idx) => (
                  <motion.tr key={bus._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.04 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                          <FiTruck className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <span className="font-semibold text-[#1e3a8a] block">{bus.busNumber}</span>
                          {bus.vehicleDetails && (
                            <span className="text-slate-400 text-xs">{bus.vehicleDetails}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-slate-700 font-medium block">{bus.driverName}</span>
                      <span className="text-slate-400 text-xs flex items-center gap-1">
                        <FiPhone className="w-3 h-3" /> {bus.driverContact}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <FiMapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> {bus.route}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className="font-semibold text-slate-700">Rs. {totalExpenses(bus).toLocaleString()}</span>
                      <span className="text-slate-400 text-xs block">{(bus.expenses || []).length} record(s)</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setDetailBus(bus)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-xs font-semibold transition-colors">
                          <FiClock className="w-3.5 h-3.5" /> History
                        </button>
                        {canEdit && (
                          <>
                            <button onClick={() => openEdit(bus)}
                              className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteBus(bus._id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {buses.length === 0 && (
            <div className="text-center py-16">
              <FiTruck className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No buses recorded yet</p>
              {canEdit && (
                <p className="text-slate-400 text-sm mt-1">Click "Add Bus" to record the first bus.</p>
              )}
            </div>
          )}
        </div>
      )}
      </>
      )}

      {activeTab === 'reports' && (
        <div>
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex flex-wrap gap-4 items-end shadow-sm">
            <div>
              <label className={labelClass}>Bus</label>
              <select className={inputClass} value={reportBusId}
                onChange={(e) => setReportBusId(e.target.value)}>
                <option value="">All Buses (Fleet)</option>
                {buses.map((b) => <option key={b._id} value={b._id}>{b.busNumber} — {b.driverName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>From</label>
              <input type="date" className={inputClass} value={reportFrom}
                onChange={(e) => setReportFrom(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>To</label>
              <input type="date" className={inputClass} value={reportTo}
                onChange={(e) => setReportTo(e.target.value)} />
            </div>
            <button onClick={fetchReport}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              <FiBarChart2 className="w-4 h-4" /> Generate Report
            </button>
            {(reportFrom || reportTo || reportBusId) && (
              <button onClick={() => { setReportFrom(''); setReportTo(''); setReportBusId(''); }}
                className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
                Clear
              </button>
            )}
          </div>

          {reportLoading ? <LoadingSpinner /> : (
            <>
              {!reportBusId && fleetReport && (
                <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50/50 border-b border-blue-100">
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Bus</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Driver</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Fuel</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Repair &amp; Maintenance</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Service</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Other</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fleetReport.buses.map((row) => (
                          <tr key={row.busId} className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors cursor-pointer"
                            onClick={() => setReportBusId(row.busId)}>
                            <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{row.busNumber}</td>
                            <td className="px-5 py-4 text-slate-500">{row.driverName}</td>
                            <td className="px-5 py-4 text-right text-slate-600">Rs. {row.totalsByType.Fuel.toLocaleString()}</td>
                            <td className="px-5 py-4 text-right text-slate-600">Rs. {row.totalsByType['Repair & Maintenance'].toLocaleString()}</td>
                            <td className="px-5 py-4 text-right text-slate-600 hidden md:table-cell">Rs. {row.totalsByType.Service.toLocaleString()}</td>
                            <td className="px-5 py-4 text-right text-slate-600 hidden md:table-cell">Rs. {row.totalsByType.Other.toLocaleString()}</td>
                            <td className="px-5 py-4 text-right font-semibold text-[#1e3a8a]">Rs. {row.total.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-blue-50/70 border-t-2 border-blue-100">
                          <td className="px-5 py-4 font-bold text-[#1e3a8a]" colSpan={2}>Fleet Total</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">Rs. {fleetReport.fleetTotals.Fuel.toLocaleString()}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">Rs. {fleetReport.fleetTotals['Repair & Maintenance'].toLocaleString()}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a] hidden md:table-cell">Rs. {fleetReport.fleetTotals.Service.toLocaleString()}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a] hidden md:table-cell">Rs. {fleetReport.fleetTotals.Other.toLocaleString()}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">Rs. {fleetReport.fleetGrandTotal.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {fleetReport.buses.length === 0 && (
                    <div className="text-center py-16">
                      <FiBarChart2 className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">No buses to report on</p>
                    </div>
                  )}
                </div>
              )}

              {reportBusId && busReport && (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                    <div className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm col-span-2 sm:col-span-1">
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Bus</p>
                      <p className="text-lg font-bold text-[#1e3a8a] mt-1">{busReport.bus.busNumber}</p>
                      <p className="text-slate-400 text-xs mt-0.5">{busReport.bus.driverName}</p>
                    </div>
                    {EXPENSE_TYPES.map((type) => (
                      <div key={type} className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm">
                        <p className="text-slate-400 text-xs uppercase tracking-wider">{type}</p>
                        <p className="text-lg font-bold text-[#1e3a8a] mt-1">Rs. {(busReport.totalsByType[type] || 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 mb-6 flex items-center justify-between">
                    <span className="text-slate-500 text-sm font-semibold">Grand Total ({busReport.expenses.length} record{busReport.expenses.length !== 1 ? 's' : ''})</span>
                    <span className="text-xl font-bold text-[#1e3a8a]">Rs. {busReport.grandTotal.toLocaleString()}</span>
                  </div>

                  <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-blue-50/50 border-b border-blue-100">
                            <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Type</th>
                            <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Description</th>
                            <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Amount</th>
                            <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Date</th>
                            <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Added By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {busReport.expenses.map((exp) => (
                            <tr key={exp._id} className="border-b border-blue-50">
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-semibold">
                                  <FiDollarSign className="w-3 h-3" /> {exp.type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600">{exp.description || '—'}</td>
                              <td className="px-4 py-3 font-semibold text-slate-700">Rs. {Number(exp.amount).toLocaleString()}</td>
                              <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{formatDate(exp.date)}</td>
                              <td className="px-4 py-3 hidden sm:table-cell text-slate-400 text-xs">{exp.addedBy?.name || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {busReport.expenses.length === 0 && (
                      <div className="text-center py-16">
                        <FiBarChart2 className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">No expense records in this range</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add/Edit Bus Modal */}
      <Modal isOpen={busModalOpen} onClose={() => setBusModalOpen(false)}
        title={editingBus ? 'Edit Bus' : 'Add Bus'} size="lg">
        <form onSubmit={handleBusSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Bus Number *</label>
              <input className={inputClass} required value={busForm.busNumber}
                onChange={(e) => setBusForm({ ...busForm, busNumber: e.target.value })}
                placeholder="e.g. BA 2 KHA 1234" />
            </div>
            <div>
              <label className={labelClass}>Driver Name *</label>
              <input className={inputClass} required value={busForm.driverName}
                onChange={(e) => setBusForm({ ...busForm, driverName: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Driver Contact *</label>
              <input className={inputClass} required value={busForm.driverContact}
                onChange={(e) => setBusForm({ ...busForm, driverContact: e.target.value })}
                placeholder="98XXXXXXXX" />
            </div>
            <div>
              <label className={labelClass}>Route *</label>
              <input className={inputClass} required value={busForm.route}
                onChange={(e) => setBusForm({ ...busForm, route: e.target.value })}
                placeholder="e.g. School - Chowk - Bazar" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Vehicle Details</label>
            <input className={inputClass} value={busForm.vehicleDetails}
              onChange={(e) => setBusForm({ ...busForm, vehicleDetails: e.target.value })}
              placeholder="Model, capacity, registration year..." />
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea className={inputClass} rows={2} value={busForm.remarks}
              onChange={(e) => setBusForm({ ...busForm, remarks: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setBusModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {saving ? 'Saving...' : editingBus ? 'Update Bus' : 'Add Bus'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Bus History Modal */}
      <Modal isOpen={!!detailBus} onClose={() => setDetailBus(null)}
        title={detailBus ? `${detailBus.busNumber} — Expense & Service History` : ''} size="xl">
        {detailBus && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-blue-50/60 rounded-xl p-3 border border-blue-100">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Driver</p>
                <p className="font-semibold text-[#1e3a8a]">{detailBus.driverName}</p>
              </div>
              <div className="bg-blue-50/60 rounded-xl p-3 border border-blue-100">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Contact</p>
                <p className="font-semibold text-[#1e3a8a]">{detailBus.driverContact}</p>
              </div>
              <div className="bg-blue-50/60 rounded-xl p-3 border border-blue-100">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Route</p>
                <p className="font-semibold text-[#1e3a8a]">{detailBus.route}</p>
              </div>
              <div className="bg-blue-50/60 rounded-xl p-3 border border-blue-100">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Total Spent</p>
                <p className="font-semibold text-[#1e3a8a]">Rs. {totalExpenses(detailBus).toLocaleString()}</p>
              </div>
            </div>

            {canEdit && (
              <form onSubmit={handleAddExpense}
                className="bg-blue-50/40 border border-blue-100 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                <div>
                  <label className={labelClass}>Type *</label>
                  <select className={inputClass} value={expenseForm.type}
                    onChange={(e) => setExpenseForm({ ...expenseForm, type: e.target.value })}>
                    {EXPENSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Amount (Rs.) *</label>
                  <input className={inputClass} type="number" min="0" step="0.01" required
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
                </div>
                <div>
                  <label className={labelClass}>Date</label>
                  <input className={inputClass} type="date" value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} />
                </div>
                <div>
                  <label className={labelClass}>Description</label>
                  <input className={inputClass} value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    placeholder="e.g. Diesel 40L" />
                </div>
                <button type="submit" disabled={addingExpense}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
                  <FiPlus className="w-4 h-4" /> {addingExpense ? 'Adding...' : 'Add Record'}
                </button>
              </form>
            )}

            <div className="overflow-x-auto border border-blue-100 rounded-2xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50/50 border-b border-blue-100">
                    <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Type</th>
                    <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Description</th>
                    <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Amount</th>
                    <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Date</th>
                    <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Added By</th>
                    {canEdit && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody>
                  {[...(detailBus.expenses || [])]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((exp) => (
                      <tr key={exp._id} className="border-b border-blue-50">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-semibold">
                            <FiDollarSign className="w-3 h-3" /> {exp.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{exp.description || '—'}</td>
                        <td className="px-4 py-3 font-semibold text-slate-700">Rs. {Number(exp.amount).toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{formatDate(exp.date)}</td>
                        <td className="px-4 py-3 hidden sm:table-cell text-slate-400 text-xs">{exp.addedBy?.name || '—'}</td>
                        {canEdit && (
                          <td className="px-4 py-3">
                            <button onClick={() => handleDeleteExpense(exp._id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
              {(detailBus.expenses || []).length === 0 && (
                <p className="text-center text-slate-400 text-sm py-8">No expense records yet.</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default AdminBuses;
